/**
 * Confirmation modals for Approve and Reject actions
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import styled, { useTheme } from "styled-components";
import { Modal } from "../../../../components/Modal/Modal";
import {
  PrimaryButtonMediumV2,
  SecondaryButtonMediumV2,
  DestructiveButtonMediumV2,
} from "../../../../components/Buttons/Buttons";
import {
  TextXlSemibold,
  TextBaseRegular,
} from "../../../../components/Typography/TypographyV2";
import { TextArea } from "../../../../components/TextArea/TextArea";
import {
  FlagIcon,
  FlagXIcon,
  ErrorIcon,
} from "../../../../components/Icons/Icons";
import { useApprovalMutations } from "./hooks/useApprovalMutations";
import axios, { AxiosError } from "axios";
import { useStoreState } from "../../../../util/util";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { approvalConfirmatiomMessage } from "./utils";

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
  padding: ${({ theme }) => theme.resolve("spacing_4xl")}
    ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const WarningSection = styled.div`
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")} 0;
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};

  > * {
    flex: 1;
  }
`;

// =============================================================================
// Approve Confirmation Modal
// =============================================================================

interface ApproveConfirmationModalProps {
  approvalId: string;
  stageName: string;
  stageDisplayName: string;
  isLastStage?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function ApproveConfirmationModal({
  approvalId,
  stageName,
  stageDisplayName,
  isLastStage,
  onClose,
  onConfirm,
}: ApproveConfirmationModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { submitDecision, isSubmitting } = useApprovalMutations();

  const handleApprove = async () => {
    if (isSubmitting) return;
    try {
      await submitDecision(approvalId, {
        stage: stageName,
        decision: "approve",
        comment: "",
      });
      onConfirm();
    } catch (err) {
      // Error handled in hook
    }
  };

  const getApproveMessage = () => {
    if (isLastStage) {
      return t(
        "You are about to approve this SKU for the {{stage}} stage. This action will mark the SKU as approved and cannot be undone.",
        { stage: stageDisplayName }
      );
    }
    return t(
      "You are about to approve this SKU for the {{stage}} stage. This action will advance the approval to the next stage and cannot be undone.",
      { stage: stageDisplayName }
    );
  };

  return (
    <Modal
      show={true}
      closeModal={onClose}
      overlay={true}
      modalWidth={theme.resolve("width_sm")}
    >
      <ModalContent>
        <ModalHeader>
          <TextXlSemibold>{t("Approve this SKU?")}</TextXlSemibold>
        </ModalHeader>

        <ContentSection>
          <WarningSection>
            <WarningContent>
              <IconWrapper>
                <FlagIcon
                  width={40}
                  height={40}
                  fill={theme.resolve("color_icon_success")}
                />
              </IconWrapper>
              <TextBaseRegular>{getApproveMessage()}</TextBaseRegular>
            </WarningContent>
          </WarningSection>
        </ContentSection>

        <ModalActions>
          <SecondaryButtonMediumV2 onClick={onClose}>
            {t("Cancel")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonMediumV2
            onClick={handleApprove}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t("Approve")}
          </PrimaryButtonMediumV2>
        </ModalActions>
      </ModalContent>
    </Modal>
  );
}

// =============================================================================
// Reject Confirmation Modal
// =============================================================================

interface RejectConfirmationModalProps {
  approvalId: string;
  stageName: string;
  stageDisplayName: string;
  onClose: () => void;
  onConfirm: () => void;
}

interface RejectFormData {
  reason: string;
}

export function RejectConfirmationModal({
  approvalId,
  stageName,
  stageDisplayName,
  onClose,
  onConfirm,
}: RejectConfirmationModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { submitDecision, isSubmitting } = useApprovalMutations();
  const { register, handleSubmit, formState, errors, watch } =
    useForm<RejectFormData>();
  const reasonValue = watch("reason", "");

  const onSubmit = async (data: RejectFormData) => {
    if (isSubmitting) return;
    try {
      await submitDecision(approvalId, {
        stage: stageName,
        decision: "reject",
        comment: data.reason.trim(),
      });
      onConfirm();
    } catch (err) {
      // Error handled in hook
    }
  };

  return (
    <Modal
      show={true}
      closeModal={onClose}
      overlay={true}
      modalWidth={theme.resolve("width_sm")}
    >
      <ModalContent>
        <ModalHeader>
          <TextXlSemibold>{t("Reject this SKU?")}</TextXlSemibold>
        </ModalHeader>

        <ContentSection>
          <WarningSection>
            <WarningContent>
              <IconWrapper>
                <FlagXIcon width={40} height={40} />
              </IconWrapper>
              <TextBaseRegular>
                {t(
                  'You are about to reject this SKU for the {{stage}} stage. This SKU will be marked as "Rejected" and cannot be undone. Please provide reason for rejection below.',
                  { stage: stageDisplayName }
                )}
              </TextBaseRegular>
            </WarningContent>
          </WarningSection>

          <TextArea
            name="reason"
            label={t("Reason for Rejection *")}
            theref={register({ required: true })}
            formState={formState}
            required={true}
            errors={errors}
            rows={3}
          />
        </ContentSection>

        <ModalActions>
          <SecondaryButtonMediumV2 onClick={onClose}>
            {t("Cancel")}
          </SecondaryButtonMediumV2>
          <DestructiveButtonMediumV2
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={isSubmitting || !reasonValue?.trim()}
          >
            {t("Reject")}
          </DestructiveButtonMediumV2>
        </ModalActions>
      </ModalContent>
    </Modal>
  );
}

// =============================================================================
// Resubmit Approval Modal
// =============================================================================

interface ResubmitConfirmationModalProps {
  productId: string;
  skuId: string;
  onClose: () => void;
  onConfirm: () => void;
}

interface ResubmitFormData {
  reason: string;
}

export function ResubmitConfirmationModal({
  productId,
  skuId,
  onClose,
  onConfirm,
}: ResubmitConfirmationModalProps) {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { notifySuccess, notifyError } = useNotifications();
  const theme = useTheme();
  const { register, handleSubmit, formState, errors, watch } =
    useForm<ResubmitFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reasonValue = watch("reason", "");

  const onSubmit = async (data: ResubmitFormData) => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await axios.post(
        `/v2/storefronts/${storefront_id}/integrations/products/${productId}/approvals/sku/${skuId}/resubmit`,
        {
          comment: data.reason.trim(),
        }
      );
      notifySuccess(t("Resubmission submitted successfully"));
      onConfirm();
    } catch (err) {
      const axiosError = err as AxiosError;
      notifyError(
        axiosError.response?.data?.message ||
          t("Failed to submit resubmission"),
        { error: axiosError }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={true}
      closeModal={onClose}
      overlay={true}
      modalWidth={theme.resolve("width_sm")}
    >
      <ModalContent>
        <ModalHeader>
          <TextXlSemibold>{t("Resubmit for Approval")}</TextXlSemibold>
        </ModalHeader>

        <ContentSection>
          <WarningSection>
            <WarningContent>
              <IconWrapper>
                <FlagIcon
                  width={40}
                  height={40}
                  fill={theme.resolve("color_icon_success")}
                />
              </IconWrapper>
              <TextBaseRegular>
                {approvalConfirmatiomMessage(t)}
              </TextBaseRegular>
            </WarningContent>
          </WarningSection>

          <TextArea
            name="reason"
            label={t("Reason for Resubmission *")}
            theref={register({ required: true })}
            formState={formState}
            required={true}
            errors={errors}
            rows={3}
          />
        </ContentSection>

        <ModalActions>
          <SecondaryButtonMediumV2 onClick={onClose}>
            {t("Cancel")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonMediumV2
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={isSubmitting || !reasonValue?.trim()}
          >
            {t("Resubmit")}
          </PrimaryButtonMediumV2>
        </ModalActions>
      </ModalContent>
    </Modal>
  );
}

// =============================================================================
// Duplicate SKU / Item Modals
// =============================================================================

interface DuplicateEntityModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  message: ReactNode;
}

/** Shared warning-modal shell for "you're about to save a duplicate" cases. */
function DuplicateEntityModal({
  show,
  onClose,
  title,
  message,
}: DuplicateEntityModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Modal
      show={show}
      closeModal={onClose}
      overlay={true}
      modalWidth={theme.resolve("width_sm")}
    >
      <ModalContent>
        <ModalHeader>
          <TextXlSemibold>{title}</TextXlSemibold>
        </ModalHeader>

        <ContentSection>
          <WarningSection>
            <WarningContent>
              <IconWrapper>
                <ErrorIcon width={40} height={40} fill={theme.errorColor} />
              </IconWrapper>
              <TextBaseRegular>{message}</TextBaseRegular>
            </WarningContent>
          </WarningSection>
        </ContentSection>

        <ModalActions>
          <SecondaryButtonMediumV2 onClick={onClose}>
            {t("Close")}
          </SecondaryButtonMediumV2>
        </ModalActions>
      </ModalContent>
    </Modal>
  );
}

interface DuplicateSKUModalProps {
  matchingSkuId: string | null;
  skuIdDisplayName: string;
  onClose: () => void;
}

export function DuplicateSKUModal({
  matchingSkuId,
  onClose,
  skuIdDisplayName,
}: DuplicateSKUModalProps) {
  const { t } = useTranslation();

  return (
    <DuplicateEntityModal
      show={true}
      onClose={onClose}
      title={t("Duplicate SKU Detected")}
      message={
        matchingSkuId ? (
          <>
            {t(
              "This SKU is identical to an existing SKU with {{skuIdDisplayName}}: ",
              { skuIdDisplayName }
            )}
            <strong>{t("{{skuId}}", { skuId: matchingSkuId })}</strong>
            {t(". Please modify at least one attribute before saving.")}
          </>
        ) : (
          t(
            "This SKU is identical to an existing SKU. Please modify at least one attribute before saving."
          )
        )
      }
    />
  );
}

export function DuplicateItemModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <DuplicateEntityModal
      show={show}
      onClose={onClose}
      title={t("Duplicate Item Detected")}
      message={t(
        "This item is identical to an existing item. Please modify at least one attribute before saving."
      )}
    />
  );
}
